import { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import httpclient from "../../../../Utils";

const PublicTrackingSearchSection = ({
  orderId,
  setOrderId,
  handleSearch,
  handleKeyPress,
  handleClearOrderId,
  loading,
  error,
  searched,
  driverId,
  setDriverId,
}) => {
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  useEffect(() => {
    const fetchBranches = async () => {
      setBranchesLoading(true);
      try {
        const res = await httpclient.get("/delivery/branch-list");
        if (res.data.status === 200) {
          setBranches(res.data.data || []);
        } else {
          setBranches([]);
        }
      } catch (err) {
        setBranches([]);
      } finally {
        setBranchesLoading(false);
      }
    };

    fetchBranches();
  }, []);

  const handleClear = () => {
    setSelectedBranch(null);
    setDrivers([]);
    setSelectedDriver(null);
    setDriverId("");
    if (handleClearOrderId) {
      handleClearOrderId();
    } else {
      setOrderId("");
    }
  };

  const handleOrderIdChange = (e) => {
    const value = e.target.value;
    setOrderId(value);
    // Typing directly into Invoice/Customer No. is a separate search path from
    // Branch + Driver, so manual entry always clears any driver selection -
    // this also drives disabling Branch/Driver below.
    setSelectedDriver(null);
    setDriverId("");
  };

  // Invoice search and Branch/Driver search are mutually exclusive: once the
  // user has typed a value manually (i.e. not populated by picking a driver),
  // Branch/Driver should be disabled until the invoice field is cleared.
  const isManualInvoiceEntry = !!orderId && !selectedDriver;

  const handleBranchChange = async (event, branch) => {
    setSelectedBranch(branch);
    setSelectedDriver(null);
    setDriverId("");
    setDrivers([]);
    setOrderId("");

    if (!branch?.branch_code) {
      return;
    }

    setDriversLoading(true);
    try {
      const res = await httpclient.get(
        `/delivery/driver-list/${branch.branch_code}`
      );
      if (res.data.status === 200) {
        setDrivers(res.data.data || []);
      } else {
        setDrivers([]);
      }
    } catch (err) {
      setDrivers([]);
    } finally {
      setDriversLoading(false);
    }
  };

  const handleDriverChange = (event, driver) => {
    setSelectedDriver(driver);
    setDriverId(driver?.id || "");
    if (driver?.employee_code) {
      setOrderId(driver.employee_code);
    } else {
      setOrderId("");
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        mb: 4,
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <Box sx={{ width: { xs: "100%", sm: "30%" } }}>
          <TextField
            fullWidth
            label={selectedDriver ? "Driver Code" : "Invoice/Customer No."}
            variant="outlined"
            value={orderId}
            onChange={handleOrderIdChange}
            onKeyPress={handleKeyPress}
            placeholder={selectedDriver ? "Driver code" : "e.g., N2493849"}
            error={!!error && searched}
            disabled={loading || !!selectedBranch}
            InputProps={{
              endAdornment: orderId && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClear}
                    edge="end"
                    size="small"
                    disabled={loading || !!selectedBranch}
                    sx={{ ml: -1 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                "& .MuiInputBase-input": {
                  flex: 1,
                },
                "& .MuiInputAdornment-positionEnd": {
                  marginLeft: "auto",
                  marginRight: 0,
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                  borderWidth: "2px",
                },
              },
            }}
          />
        </Box>

        <Box sx={{ width: { xs: "100%", sm: "20%" } }}>
          <Autocomplete
            options={branches}
            getOptionLabel={(branch) => branch?.branch_name || ""}
            isOptionEqualToValue={(option, value) =>
              option.branch_code === value.branch_code
            }
            value={selectedBranch}
            onChange={handleBranchChange}
            loading={branchesLoading}
            disabled={loading || isManualInvoiceEntry}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Branch"
                placeholder="Select branch"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {branchesLoading ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: 56,
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                      borderWidth: "2px",
                    },
                  },
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ width: { xs: "100%", sm: "25%" } }}>
          <Autocomplete
            options={drivers}
            getOptionLabel={(driver) => driver?.full_name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedDriver}
            onChange={handleDriverChange}
            loading={driversLoading}
            disabled={loading || !selectedBranch || isManualInvoiceEntry}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Driver"
                placeholder={
                  isManualInvoiceEntry
                    ? "Clear Invoice No. to use"
                    : selectedBranch
                    ? "Search driver"
                    : "Select branch first"
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {driversLoading ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: 56,
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                      borderWidth: "2px",
                    },
                  },
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ width: { xs: "100%", sm: "20%" } }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={
              loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <SearchIcon />
              )
            }
            onClick={handleSearch}
            disabled={loading}
            sx={{
              height: 56,
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              textTransform: "none",
              fontWeight: "600",
              fontSize: "1rem",
            }}
          >
            <Typography
              variant="button"
              fontWeight="600"
              sx={{ whiteSpace: "nowrap" }}
            >
              {loading ? "Tracking..." : "Track Order"}
            </Typography>
          </Button>
        </Box>
      </Box>
      {error && searched && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: "8px" }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
};

export default PublicTrackingSearchSection;
